import apiClient from "@/shared/api/apiClient";

export const getUserList = async (params) => {
  try {
    const { data } = await apiClient.get("/users/list/", {
      params,
    });
    return data;
  } catch (e) {
    console.error("getUserList failed", e);
  }
};

export const getUserItem = async (id, { signal } = {}) => {
  try {
    const { data } = await apiClient.get(`/users/${id}/`, { signal });
    return data;
  } catch (e) {
    if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") throw e;
    console.error("getUserItem failed", e);
  }
};

/**
 * Загружает данные для UserCard.
 *
 * Endpoint: `GET /users/{user_id}/`
 * (тот же, что у getUserItem; до появления выделенного `/card` endpoint
 * данные маппятся в shape UserCard, неизвестные поля → null/{}).
 *
 * Поддерживает AbortSignal. Прокидывает ошибку отмены наружу, чтобы
 * cache мог отличить cancellation от настоящей ошибки.
 *
 * @param {string} userId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<import('../model/types').UserCard>}
 */
export const getUserCard = async (userId, { signal } = {}) => {
  const response = await apiClient
    .get(`/users/${userId}/`, { signal })
    .then((r) => r.data);

  const raw = response?.payload ?? response ?? {};

  const positionFromRoles = Array.isArray(raw?.role_codes)
    ? raw.role_codes[0]
    : null;
  const positionFromRoleArr =
    Array.isArray(raw?.roles) && raw.roles.length
      ? raw.roles[0]?.name ?? raw.roles[0]?.code
      : null;

  const organizationName =
    raw?.organization?.name ??
    raw?.organization?.short_name ??
    raw?.organization_name ??
    null;
  const organizationId = raw?.organization?.id ?? raw?.organization_id ?? null;

  return {
    id: raw?.uuid ?? raw?.id ?? userId,
    identifier: raw?.identifier ?? null,
    first_name: raw?.first_name ?? null,
    last_name: raw?.last_name ?? null,
    position: raw?.position ?? positionFromRoleArr ?? positionFromRoles ?? null,
    organization_id: organizationId,
    organization_name: organizationName,
    location: raw?.location ?? null,
    bio: raw?.bio ?? null,
    email: raw?.email ?? null,
    phone: raw?.phone ?? null,
    date_of_birth: raw?.date_of_birth ?? raw?.birth_date ?? null,
    joined_at: raw?.joined_at ?? raw?.created_at ?? null,
    avatar_url: raw?.avatar_url ?? raw?.photo_url ?? null,
    cover_url: raw?.cover_url ?? null,
    socials: raw?.socials ?? {},
  };
};

export const createUser = async (params) => {
  const result = await apiClient.post(`/users/`, {
    ...params,
  });
  return result.data;
};

export const setEmployee = async (id, params) => {
  const result = await apiClient.post(`/users/${id}/employee/`, {
    ...params,
  });
  return result.data;
};

export const updateUser = async (id, params) => {
  const { data } = await apiClient.put(`/users/${id}`, {
    ...params,
  });
  return data;
};

export const removeUser = async (id) => {
  const { data } = await apiClient.delete(`/users/${id}`);
  return data;
};

export const getNotaryList = async (params) => {
  try {
    const { data } = await apiClient.get("/users/notary-list/", {
      params,
    });
    return data;
  } catch (e) {
    console.error("getNotaryList failed", e);
  }
};

export const getUserMeOrganizations = async () => {
  try {
    const { data } = await apiClient.get("/users/me/organizations/");
    return data;
  } catch (e) {
    console.error("getUserMeOrganizations failed", e);
  }
};

export const updateUserMeContact = async (params) => {
  const { data } = await apiClient.patch("/users/me/contact/", { ...params });
  return data;
};

export const bulkAssignUsersOrganization = async ({
  user_uuids,
  organization_id,
}) => {
  const { data } = await apiClient.post("/users/bulk-organization/", {
    user_uuids,
    organization_id,
  });
  return data;
};
